/**
 * Feb 2026 · Stock Register.
 *
 * Reads `/api/inventory/stock-by-godown` — the ledger-derived pivot of
 * on-hand qty per (rm × godown × quality_status). Filters:
 *   • Godown / Department
 *   • Quality Status (Passed / Awaiting Inspection / Rejected / Hold)
 *   • Text search across RM code, name, godown name
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MagnifyingGlassIcon, WarehouseIcon, FunnelIcon, PackageIcon, FactoryIcon, StorefrontIcon } from "@phosphor-icons/react";
import { fmtQty } from "@/lib/uom";
import { fmtDateTimeIST } from "@/lib/datetime";

const QUALITY = ["All", "Passed", "Awaiting Inspection", "Rejected", "Hold"];

// Feb 2026 · Godown-Category tabs. Backend `godown.kind` already carries
// RM / WIP / FG — this bar just gives Management a one-click toggle to
// look at only one pool at a time. `all` shows every kind.
const CATEGORY_TABS = [
  { key: "all", label: "All Stock",  icon: WarehouseIcon,  hint: "Every godown"          },
  { key: "RM",  label: "Warehouse",  icon: StorefrontIcon, hint: "Raw Material Store"    },
  { key: "WIP", label: "Floor Stock",icon: FactoryIcon,    hint: "Work-In-Progress"      },
  { key: "FG",  label: "Finished",   icon: PackageIcon,    hint: "Finished Goods Store"  },
];

const badgeCls = (q) => ({
  "Passed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Awaiting Inspection": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Rejected": "bg-red-500/15 text-red-300 border-red-500/30",
  "Hold": "bg-sky-500/15 text-sky-300 border-sky-500/30",
}[q] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30");

export default function StockRegister() {
  const [rows, setRows] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [godownId, setGodownId] = useState("");
  const [quality, setQuality] = useState("All");
  // Feb 2026 · Category tab (all|RM|WIP|FG). Filters the pivot to just
  // one godown-kind so Management sees Warehouse vs Floor stock at a
  // glance without hunting through the godown dropdown.
  const [category, setCategory] = useState("all");
  // Feb 2026 · RM Drill-Down · when the operator taps a "Stock by
  // Category" row on the Inventory Executive Dashboard we open the
  // Register pre-filtered to that RM category. The value is read once
  // from `?category=` and shown as a dismissable chip above the grid.
  const [rmCategory, setRmCategory] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("category") || "";
    } catch { return ""; }
  });
  const clearRmCategory = () => {
    setRmCategory("");
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("category");
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
  };
  // Mar 3, 2026 · Chip Drill-Down · in-Register GRN/OPB drawer state.
  // `docPeek = { kind: "GRN"|"OPB", no: "GRN-…" }` opens the right-side
  // sheet; `docPeekData` holds the fetched detail; `docPeekLoading`
  // gates the spinner. Closing resets everything.
  const [docPeek, setDocPeek] = useState(null);
  const [docPeekData, setDocPeekData] = useState(null);
  const [docPeekLoading, setDocPeekLoading] = useState(false);

  const openDocPeek = async (kind, no) => {
    setDocPeek({ kind, no });
    setDocPeekData(null);
    setDocPeekLoading(true);
    try {
      if (kind === "GRN") {
        const { data } = await api.get("/inventory-lots/");
        const lot = (data || []).find(l => l.lot_no === no || l.grn_no === no);
        setDocPeekData(lot || { _missing: true });
      } else {
        const { data } = await api.get("/opening-stock-entries/");
        const opb = (data || []).find(o => o.opb_no === no);
        setDocPeekData(opb || { _missing: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch doc");
    } finally { setDocPeekLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = quality !== "All" ? { quality } : {};
      const [{ data }, { data: gs }] = await Promise.all([
        api.get("/inventory/stock-by-godown", { params }),
        api.get("/godowns/"),
      ]);
      setRows(data || []);
      setGodowns(gs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stock register");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [quality]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rmCatLower = rmCategory.trim().toLowerCase();
    return rows.filter(r => {
      if (godownId && r.godown_id !== godownId) return false;
      if (category !== "all" && (r.godown_kind || "").toUpperCase() !== category) return false;
      if (rmCatLower && (r.rm_category || "").toLowerCase() !== rmCatLower) return false;
      if (!needle) return true;
      return [r.rm_code, r.rm_name, r.godown_name, r.department, r.rm_category]
        .join(" ").toLowerCase().includes(needle);
    });
  }, [rows, q, godownId, category, rmCategory]);

  // Feb 2026 · Per-pool KPI totals — three at-a-glance tiles for the
  // "Warehouse vs Floor Stock" dashboard split the client asked for.
  const poolTotals = useMemo(() => {
    const t = { RM: 0, WIP: 0, FG: 0 };
    for (const r of rows) {
      const k = (r.godown_kind || "").toUpperCase();
      if (t[k] !== undefined) t[k] += Number(r.on_hand || 0);
    }
    return t;
  }, [rows]);

  const totalOnHand = filtered.reduce((s, r) => s + Number(r.on_hand || 0), 0);

  return (
    <div className="p-6 space-y-4" data-testid="stock-register-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Stock Register</h1>
          <p className="text-xs text-zinc-500 mt-1">On-hand quantity per Raw Material × Godown × Quality Status · driven by the inventory ledger</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sr-pool-tiles">
        {CATEGORY_TABS.map(t => {
          const Icon = t.icon;
          const value = t.key === "all"
            ? poolTotals.RM + poolTotals.WIP + poolTotals.FG
            : (poolTotals[t.key] || 0);
          const active = category === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setCategory(t.key)}
              data-testid={`sr-tab-${t.key}`}
              className={`text-left tactical-card p-4 transition-colors ${active ? "border-[var(--accent)] bg-[var(--accent)]/5" : "hover:border-[var(--accent)]/40"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-[10px]">{t.label}</div>
                <Icon size={14} weight="bold" className={active ? "text-[var(--accent)]" : "text-zinc-500"} />
              </div>
              <div className="font-mono text-2xl font-semibold text-white">{fmtQty(value, "KG")}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{t.hint}</div>
            </button>
          );
        })}
      </div>

      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="sr-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search RM code / name / godown…" className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          </div>
          <select data-testid="sr-godown" value={godownId} onChange={e => setGodownId(e.target.value)} className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
            <option value="">All Godowns</option>
            {godowns.filter(g => g.is_active !== false).map(g => <option key={g.id} value={g.id}>{g.name} · {g.kind}</option>)}
          </select>
          <select data-testid="sr-quality" value={quality} onChange={e => setQuality(e.target.value)} className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
            {QUALITY.map(qs => <option key={qs} value={qs}>{qs}</option>)}
          </select>
          {rmCategory && (
            <button
              type="button"
              data-testid="sr-rm-category-chip"
              onClick={clearRmCategory}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[var(--accent)]/50 bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-mono uppercase tracking-widest hover:bg-[var(--accent)]/20 transition"
              title="Clear category filter"
            >
              Category: <span className="text-white">{rmCategory}</span>
              <span className="text-zinc-400 hover:text-white">×</span>
            </button>
          )}
          <span className="ml-auto text-xs text-zinc-500">{filtered.length} row(s) · total on-hand <b className="text-white font-mono">{totalOnHand.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b></span>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">RM Code</th>
                <th className="px-3 py-2 text-left">Name / Category</th>
                <th className="px-3 py-2 text-left">Godown · Department</th>
                <th className="px-3 py-2 text-left">Source Doc</th>
                <th className="px-3 py-2 text-left">Batch No</th>
                <th className="px-3 py-2 text-left">Quality</th>
                <th className="px-3 py-2 text-right">On Hand</th>
                <th className="px-3 py-2 text-left">UOM</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-xs">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-xs">No stock matches the current filters</td></tr>}
              {filtered.map((r, i) => (
                <tr key={i} data-testid={`sr-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                  <td className="px-3 py-2 font-mono text-[var(--accent)] whitespace-nowrap text-xs">{r.rm_code}</td>
                  <td className="px-3 py-2 text-xs">{r.rm_name}<div className="text-[10px] text-zinc-500">{r.rm_category}</div></td>
                  <td className="px-3 py-2 text-xs">{r.godown_name}<div className="text-[10px] text-zinc-500">{r.department || "—"} · {r.godown_kind}</div></td>
                  <td className="px-3 py-2 max-w-[220px]" data-testid={`sr-source-${i}`}>
                    {(() => {
                      // Mar 3, 2026 · Show the source docs (GRN/OPB) that
                      // minted this stock bucket as clickable chips so the
                      // Register plugs into the doc-number spine.
                      const docs = r.source_docs || [];
                      if (docs.length === 0) return <span className="text-zinc-600 text-[11px]">—</span>;
                      const isGRN = (d) => d.startsWith("GRN-");
                      const linkFor = (d) => isGRN(d) ? "/inventory/purchase-receipt" : "/inventory/opening-stock";
                      return (
                        <div className="flex flex-wrap gap-1">
                          {docs.slice(0, 2).map(d => (
                            <button
                              key={d}
                              type="button"
                              data-testid={`sr-chip-${d}`}
                              onClick={() => openDocPeek(isGRN(d) ? "GRN" : "OPB", d)}
                              className={`inline-block px-1.5 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-widest ${isGRN(d) ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/15" : "border-sky-500/40 bg-sky-500/5 text-sky-300 hover:bg-sky-500/15"} cursor-pointer transition`}
                            >{d}</button>
                          ))}
                          {docs.length > 2 && (
                            <span className="text-[10px] font-mono text-zinc-500" title={docs.join(", ")}>+{docs.length - 2} more</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-purple-300 whitespace-nowrap max-w-[240px] truncate"
                      title={(r.batches || []).join(", ") || "—"}>
                    {(r.batches || []).length === 0
                      ? <span className="text-zinc-600">—</span>
                      : (r.batches.length === 1
                          ? r.batches[0]
                          : <span>{r.batches[0]} <span className="text-zinc-500">+{r.batches.length - 1} more</span></span>)}
                  </td>
                  <td className="px-3 py-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(r.quality_status)}`}>{r.quality_status}</span></td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">{Number(r.on_hand).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                  <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.uom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mar 3, 2026 · Source-Doc Drawer — right-hand sheet that peeks
          the GRN / OPB the chip belongs to so operators verify without
          leaving the Register. Closes on backdrop click or Esc. */}
      {docPeek && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="sr-doc-drawer">
          <div className="fixed inset-0 bg-black/60" onClick={() => setDocPeek(null)} />
          <div className="relative w-full max-w-md bg-[var(--bg-1)] border-l border-[var(--border)] h-full overflow-y-auto p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline mb-1">{docPeek.kind === "GRN" ? "Purchase Receipt" : "Opening Stock"}</div>
                <h2 className="font-display text-xl font-bold text-white tracking-tight" data-testid="sr-drawer-title">{docPeek.no}</h2>
              </div>
              <button
                data-testid="sr-drawer-close"
                onClick={() => setDocPeek(null)}
                className="h-8 w-8 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white hover:border-[var(--accent)] font-mono"
              >×</button>
            </div>

            {docPeekLoading && <div className="text-center py-8 text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading…</div>}

            {!docPeekLoading && docPeekData?._missing && (
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-4 text-amber-300 text-xs">
                {docPeek.kind} <span className="font-mono">{docPeek.no}</span> not found. It may have been voided or archived.
              </div>
            )}

            {!docPeekLoading && docPeekData && !docPeekData._missing && (
              <div className="space-y-4 text-sm" data-testid="sr-drawer-body">
                {docPeek.kind === "GRN" ? (
                  <>
                    <Field label="GRN No"        value={docPeekData.grn_no || docPeekData.lot_no} accent />
                    <Field label="Batch No"      value={docPeekData.batch_no} />
                    <Field label="Received Date" value={docPeekData.received_date} />
                    <Field label="Received Qty"  value={`${Number(docPeekData.qty_received || 0).toLocaleString("en-IN")} ${docPeekData.uom || "kg"}`} />
                    <Field label="Consumed"      value={`${Number(docPeekData.qty_used || 0).toLocaleString("en-IN")} ${docPeekData.uom || "kg"}`} />
                    <Field label="Balance"       value={`${Number((docPeekData.qty_received || 0) - (docPeekData.qty_used || 0)).toLocaleString("en-IN")} ${docPeekData.uom || "kg"}`} accent />
                    <Field label="Godown"        value={docPeekData.location} />
                    <Field label="Quality"       value={docPeekData.quality_status} />
                    {docPeekData.void && (
                      <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-3 text-red-300 text-xs">
                        <div className="font-mono uppercase tracking-widest text-[10px] mb-1">Voided</div>
                        <div>By {docPeekData.voided_by} on {fmtDateTimeIST(docPeekData.voided_at)}</div>
                        <div className="text-red-200 mt-1">{docPeekData.void_reason}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Field label="OPB No"        value={docPeekData.opb_no} accent />
                    <Field label="RM Code"       value={docPeekData.rm_code} />
                    <Field label="RM Name"       value={docPeekData.rm_name} />
                    <Field label="Batch No"      value={docPeekData.batch_no} />
                    <Field label="Opening Date"  value={docPeekData.opening_date} />
                    <Field label="Qty"           value={`${Number(docPeekData.qty || 0).toLocaleString("en-IN")} ${docPeekData.uom || "kg"}`} accent />
                    <Field label="Godown"        value={docPeekData.godown_name} />
                    <Field label="Remarks"       value={docPeekData.remarks || "—"} />
                    <Field label="Posted By"     value={docPeekData.created_by} />
                  </>
                )}
                <div className="pt-3 border-t border-[var(--border)]">
                  <a
                    href={docPeek.kind === "GRN" ? "/inventory/purchase-receipt" : "/inventory/opening-stock"}
                    className="text-xs font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                    data-testid="sr-drawer-open-full"
                  >Open full {docPeek.kind === "GRN" ? "Purchase Receipt" : "Opening Stock"} →</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Small labelled field row for the drawer body.
function Field({ label, value, accent }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-[var(--border)]/60 pb-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`col-span-2 font-mono text-sm break-all ${accent ? "text-[var(--accent)]" : "text-zinc-200"}`}>{value || "—"}</div>
    </div>
  );
}
